/** Persistent bottom navigation (`.ui-bottomnav`): the app's four main tabs. */
import { Link, useLocation } from 'react-router-dom';

export interface BottomNavItem {
  key: string;
  label: string;
  to: string;
  /**
   * Custom "is this tab active" check, for a tab whose target route isn't a
   * simple exact match (e.g. a tab that links to `/` but should read as
   * active on a whole family of routes). Defaults to an exact match on `to`.
   */
  isActive?: (pathname: string) => boolean;
}

export interface BottomNavProps {
  items: BottomNavItem[];
  ariaLabel: string;
}

/**
 * Render the sticky bottom navigation bar.
 *
 * @param props - The tabs to render and the nav landmark's accessible name.
 * @returns The nav element.
 */
export function BottomNav({ items, ariaLabel }: BottomNavProps) {
  const { pathname } = useLocation();

  return (
    <nav className="ui-bottomnav" aria-label={ariaLabel}>
      {items.map((item) => {
        const active = item.isActive ? item.isActive(pathname) : pathname === item.to;
        const classes = ['ui-bottomnav__item', active ? 'ui-bottomnav__item--active' : '']
          .filter(Boolean)
          .join(' ');
        return (
          <Link
            key={item.key}
            to={item.to}
            className={classes}
            aria-current={active ? 'page' : undefined}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}
